import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { saasExplainerVideos } from "@/data/saasExplainers";
import { getWhatsAppUrl } from "@/lib/contact";
import { contactSection } from "@/i18n/contactSection";
import "./SaaSExplainers.css";

const titleFor = (item, lang) => item.title[lang] || item.title.en;

function ExplainerCard({ item, index, total, position, active, visible, reduce, lang, labels, onOpen }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active && visible && !reduce && !document.hidden) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [active, visible, reduce]);

  return (
    <button
      type="button"
      data-testid={`saas-explainer-card-${index}`}
      data-position={position}
      className="saas-explainers__card"
      onClick={() => onOpen(index)}
      disabled={!item.src}
      aria-label={item.src ? `${labels.play} ${titleFor(item, lang)}` : undefined}
    >
      <span className="saas-explainers__card-screen" aria-hidden="true">
        {item.src ? (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="metadata"
            poster={item.poster || undefined}
            className="saas-explainers__preview"
          >
            <source src={item.src} type={item.src.toLowerCase().split("?")[0].endsWith(".webm") ? "video/webm" : "video/mp4"} />
          </video>
        ) : (
          <span className="saas-explainers__placeholder">
            <span>SAAS</span>
            <strong>{item.id}</strong>
            <span>EXPLAINER</span>
          </span>
        )}
        {item.src && <span className="saas-explainers__play-icon"><Play size={22} fill="currentColor" strokeWidth={1.5} /></span>}
      </span>
      <span className="saas-explainers__card-meta">
        <span className="saas-explainers__card-number">{String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
        <span className="saas-explainers__card-title">{titleFor(item, lang)}</span>
        <span className="saas-explainers__card-status">{item.subtitle?.[lang] || item.subtitle?.en || labels.watch}</span>
      </span>
    </button>
  );
}

export default function SaaSExplainers() {
  const { lang, t } = useLanguage();
  const labels = t.explainers;
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const dialogRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(null);
  const [remoteVideos, setRemoteVideos] = useState(null);
  const videos = remoteVideos ?? saasExplainerVideos;
  const selectedVideo = selected === null ? null : videos[selected];

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/saas-explainers`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Could not load videos")))
      .then((data) => {
        if (!cancelled && Array.isArray(data.videos)) {
          setRemoteVideos(data.videos.map((video) => ({
            id: video.id,
            title: { ar: video.title_ar, en: video.title_en, fr: video.title_fr || video.title_en },
            subtitle: { ar: video.subtitle_ar, en: video.subtitle_en, fr: video.subtitle_fr || video.subtitle_en },
            src: video.video_url,
            poster: video.poster_url,
          })));
          setActive(0);
          setSelected(null);
        }
      })
      .catch(() => { if (!cancelled) setRemoteVideos(saasExplainerVideos); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(section);
    return () => observer.disconnect();
  }, [videos.length]);

  useEffect(() => {
    if (reduce || !visible || paused || selected !== null || videos.length < 2) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setActive((current) => (current + 1) % videos.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [reduce, visible, paused, selected, videos.length]);

  useEffect(() => {
    if (selectedVideo && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
  }, [selectedVideo]);

  const move = (delta) => setActive((current) => (current + delta + videos.length) % videos.length);
  const positionFor = (index) => {
    if (index === active) return "center";
    if (videos.length === 2) return active === 0 ? "right" : "left";
    const distance = (index - active + videos.length) % videos.length;
    return distance <= videos.length / 2 ? "right" : "left";
  };

  if (videos.length === 0) return (
    <section id="saas-explainers" ref={sectionRef} data-testid="saas-explainers-section" className="saas-explainers scroll-mt-24" aria-labelledby="saas-explainers-title">
      <div className="saas-explainers__inner pb-24 text-center">
        <p className="text-sm font-bold uppercase tracking-wide">{labels.eyebrow}</p>
        <h2 id="saas-explainers-title" className="mt-6 font-display text-4xl font-bold sm:text-6xl" dir="ltr">SaaS Explainers</h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-7">{labels.awaiting}</p>
        <a href={getWhatsAppUrl(contactSection[lang].whatsappMessage)} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex min-h-12 items-center rounded-full bg-charcoal px-6 text-sm font-semibold text-white">
          {contactSection[lang].whatsappCta}
        </a>
      </div>
    </section>
  );

  return (
    <section
      id="saas-explainers"
      ref={sectionRef}
      data-testid="saas-explainers-section"
      className="saas-explainers scroll-mt-24"
      aria-labelledby="saas-explainers-title"
    >
      <div className="saas-explainers__inner">
        <div className="saas-explainers__topline">
          <span className="saas-explainers__eyebrow">{labels.eyebrow}</span>
          <span className="saas-explainers__mark" aria-hidden="true"><Play size={15} fill="currentColor" /></span>
        </div>

        <div className="saas-explainers__title-scene">
          <span className="saas-explainers__red-bar" aria-hidden="true" />
          <span className="saas-explainers__cross saas-explainers__cross--one" aria-hidden="true">+</span>
          <span className="saas-explainers__cross saas-explainers__cross--two" aria-hidden="true">+</span>
          <span className="saas-explainers__cross saas-explainers__cross--three" aria-hidden="true">+</span>
          <span className="saas-explainers__cross saas-explainers__cross--four" aria-hidden="true">+</span>
          {videos.length > 1 && <button type="button" className="saas-explainers__arrow saas-explainers__arrow--prev" aria-label={labels.previous} onClick={() => move(-1)}>
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>}
          <div className="saas-explainers__title-stack" dir="ltr">
            <h2 id="saas-explainers-title" className="sr-only">SaaS Explainers</h2>
            <span className="saas-explainers__title-layer saas-explainers__title-layer--outline" aria-hidden="true">SaaS Explainers</span>
            <span className="saas-explainers__title-layer saas-explainers__title-layer--fill-top" aria-hidden="true">SaaS Explainers</span>
            <span className="saas-explainers__title-layer saas-explainers__title-layer--fill-bottom" aria-hidden="true">SaaS Explainers</span>
          </div>
          {videos.length > 1 && <button type="button" className="saas-explainers__arrow saas-explainers__arrow--next" aria-label={labels.next} onClick={() => move(1)}>
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>}
        </div>

        <div className="saas-explainers__meta-line">
          <span>YACOUBCREATES</span>
          <span>{labels.category}</span>
        </div>
        <p className="saas-explainers__intro">{labels.intro}</p>

        <div
          className="saas-explainers__stage"
          dir="ltr"
          onPointerEnter={() => setPaused(true)}
          onPointerLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }}
        >
          <div className="saas-explainers__dial" aria-hidden="true">
            <span className="saas-explainers__dial-ring" />
            <span className="saas-explainers__dial-label">EXPLORE<br />THE WORK</span>
          </div>
          {videos.map((item, index) => (
            <ExplainerCard
              key={item.id}
              item={item}
              index={index}
              total={videos.length}
              position={positionFor(index)}
              active={active === index && selected === null}
              visible={visible}
              reduce={reduce}
              lang={lang}
              labels={labels}
              onOpen={setSelected}
            />
          ))}
          <div className="saas-explainers__counter" aria-hidden="true">
            <span>{String(active + 1).padStart(2, "0")}</span>
            <span className="saas-explainers__counter-rule" />
            <span>{String(videos.length).padStart(2, "0")}</span>
          </div>
        </div>
      </div>

      {selectedVideo && (
        <dialog
          ref={dialogRef}
          className="saas-explainers__dialog"
          aria-label={titleFor(selectedVideo, lang)}
          onClose={() => setSelected(null)}
          onClick={(event) => { if (event.target === event.currentTarget) event.currentTarget.close(); }}
        >
          <div className="saas-explainers__dialog-content">
            <div className="saas-explainers__dialog-top">
              <span>{titleFor(selectedVideo, lang)}</span>
              <button type="button" aria-label={labels.close} onClick={() => dialogRef.current?.close()}><X size={20} /></button>
            </div>
            <video controls autoPlay playsInline poster={selectedVideo.poster || undefined} src={selectedVideo.src} />
          </div>
        </dialog>
      )}
    </section>
  );
}
